import React, { useEffect, useState } from 'react';
import { DocumentDuplicateIcon, CheckIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { clientService } from '../../../../services/clientService';
import ClientSelectModal from './ClientSelectModal';

interface ConnectTabProps {
  chatbot: any;
}

const ConnectTab: React.FC<ConnectTabProps> = ({ chatbot }) => {
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  const [allClients, setAllClients] = useState<any>([])
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalValue, setModalValue] = useState("");


  const fetchClients = async () => {
    try {
      const res: any = await clientService.list({
        isKnowledgeBaseEntry: true
      });
      setAllClients(res.records || []);
    } catch (err) {
      console.error("Failed to load clients", err);
    }
  }
  const openModal = (title: string, value: string) => {
    setModalTitle(title);
    setModalValue(value);
    setModalOpen(true);
  };

  useEffect(() => {
    fetchClients()
  }, [])

  // Generate the public chat widget URL with CLIENT_ID placeholder
  const publicChatUrl = `${window.location.origin}/chat/${chatbot?.id}/CLIENT_ID`;

  // Generate iframe embed code with placeholder
  const iframeCode = `<iframe 
  src="${window.location.origin}/chat/${chatbot?.id}/CLIENT_ID" 
  width="100%" 
  height="600" 
  frameborder="0"
  style="border: none; border-radius: 8px;"
></iframe>`;

  // Generate popup widget script
  const scriptCode = `<script>
  (function() {
    window.AGENTIC_CHAT_CONFIG = {
      chatbotId: '${chatbot?.id}',
      clientId: 'CLIENT_ID'
    };
    var s = document.createElement('script');
    s.src = '${window.location.origin}/widget.js';
    s.async = true;
    document.head.appendChild(s);
  })();

  const toggleAgenticOneChatWindow = () => {
    const w = window;
    if (w.AgenticChat) {
      w.AgenticChat.toggle();
    } else {
      console.error('Chat widget is not loaded yet.');
    }
  }
</script>`;

  // Generate popup widget with floating button script
  const floatingButtonScriptCode = `<script>
  (function() {
    window.AGENTIC_CHAT_CONFIG = {
      chatbotId: '${chatbot?.id}',
      clientId: 'CLIENT_ID'
    };
    var s = document.createElement('script');
    s.src = '${window.location.origin}/floating-button-widget.js';
    s.async = true;
    document.head.appendChild(s);
  })();
</script>`;

  const copyToClipboard = async (text: string, label: string) => {
    try {
      // Modern Clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success(`${label} copied to clipboard!`);
        setCopiedItem(label);
        setTimeout(() => setCopiedItem(null), 2000);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand('copy');
          if (successful) {
            toast.success(`${label} copied to clipboard!`);
            setCopiedItem(label);
            setTimeout(() => setCopiedItem(null), 2000);
          } else {
            throw new Error('Copy command failed');
          }
        } catch (err) {
          toast.error('Failed to copy to clipboard');
          console.error('Fallback copy failed:', err);
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy to clipboard');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Instructions Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6">
        <div className="flex items-start gap-3 mb-4">
          <InformationCircleIcon className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-agentic-text-primary mb-2">
              Integration Guide
            </h3>
            <p className="text-sm text-agentic-text-secondary">
              Follow these steps to integrate the chat widget into your website.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg p-5 space-y-4">
          <div className="space-y-3">
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                1
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-agentic-text-primary mb-1">
                  Get Your Client ID
                </h4>
                <p className="text-sm text-agentic-text-secondary">
                  Contact your administrator to obtain your unique Client ID. This ID is required to track and manage your chat conversations.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                2
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-agentic-text-primary mb-1">
                  Choose Integration Method
                </h4>
                <p className="text-sm text-agentic-text-secondary">
                  Select either <strong>Iframe Embed</strong> (inline widget) or <strong>Popup Widget</strong> (floating button) based on your preference.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                3
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-agentic-text-primary mb-1">
                  Replace Placeholder
                </h4>
                <p className="text-sm text-agentic-text-secondary mb-2">
                  In the code you copy, find and replace:
                </p>
                <div className="bg-yellow-50 border border-yellow-200 rounded px-3 py-2">
                  <code className="text-xs font-mono text-yellow-900">
                    <strong>CLIENT_ID</strong>
                  </code>
                  <span className="text-xs text-yellow-800 ml-2">→ Replace with your actual Client ID</span>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                4
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-agentic-text-primary mb-1">
                  Add to Your Website
                </h4>
                <p className="text-sm text-agentic-text-secondary">
                  Paste the modified code into your website's HTML, preferably just before the closing <code className="bg-gray-100 px-1 py-0.5 rounded">&lt;/body&gt;</code> tag.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold">
                ✓
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-agentic-text-primary mb-1">
                  Test and Verify
                </h4>
                <p className="text-sm text-agentic-text-secondary">
                  Load your website and verify that the chat widget appears and functions correctly. Test sending a message to ensure everything works.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-900">
            <strong>⚠️ Important:</strong> Make sure to replace <code className="bg-white px-1.5 py-0.5 rounded font-mono text-xs">CLIENT_ID</code> with your actual Client ID before adding the code to your website. Without a valid Client ID, the chat widget will not function properly.
          </p>
        </div>
      </div>

      {/* Direct Link */}
      <div className="border rounded-lg p-4 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-agentic-text-primary">Direct Chat Link</h4>
          <div className='flex items-center'>

            <button
              onClick={() =>
                openModal("Direct Chat Link", publicChatUrl)
              }
              className="
    px-3 py-1
    text-[12px] font-medium
    text-black
    bg-white
    rounded-m
    border
    border-black-500
    rounded-md
  "
              title="Select client"
            >
              Select Client
            </button>


          </div>
        </div>
        <p className="text-sm text-agentic-text-secondary mb-3">
          Replace <code className="bg-white px-2 py-0.5 rounded font-mono text-xs">CLIENT_ID</code> with your actual Client ID.
        </p>
        <div className="flex gap-2">
          <input
            value={publicChatUrl}
            readOnly
            className="flex-1 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm font-mono"
          />
        </div>
        <div className="mt-3 p-3 bg-white rounded border">
          <p className="text-xs text-agentic-text-secondary mb-1">Example:</p>
          <code className="text-xs font-mono text-blue-600">
            {window.location.origin}/chat/{chatbot?.id}/550e8400-e29b-41d4-a716-446655440001
          </code>
        </div>
      </div>


      {/* Iframe Embed */}
      <div className="border rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium text-agentic-text-primary">Iframe Embed (Inline)</h4>
          <div className='flex items-center'>

            <button
              onClick={() => openModal("Iframe Embed Code", iframeCode)}
              title="Select client"
              className="
  px-3 py-1
  text-[12px] font-medium
  text-black
  bg-white
  rounded-m
  border
  border-black-500
  rounded-md
"
            >
              Select Client
            </button>



          </div>
        </div>
        <p className="text-sm text-agentic-text-secondary mb-3">
          Embed the chat widget inline on your website. Replace <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">CLIENT_ID</code> with your actual Client ID.
        </p>
        <pre className="p-3 bg-gray-50 border rounded text-xs overflow-auto max-h-48 relative">
          <code>{iframeCode}</code>
        </pre>
        <div className="mt-2 flex items-start gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
          <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>Remember to replace <code className="bg-white px-1 py-0.5 rounded font-mono">CLIENT_ID</code> before using this code.</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Script Embed */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-agentic-text-primary">Popup Widget (With Floating Button)</h4>
            <div className='flex items-center'>
              <button
                onClick={() =>
                  openModal("Popup Widget With Floating Button Script", floatingButtonScriptCode)
                }
                className="
    px-3 py-1
    text-[12px] font-medium
    text-black
    bg-white
    rounded-m
    border
    border-black-500
    rounded-md
  "
                title="Select client"
              >
                Select Client
              </button>

            </div>
          </div>
          <p className="text-sm text-agentic-text-secondary mb-3">
            Add as a floating widget with toggle button. Replace <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">CLIENT_ID</code> with your actual Client ID.
          </p>
          <pre className="p-3 bg-gray-50 border rounded text-xs overflow-auto max-h-48 relative">
            <code>{floatingButtonScriptCode}</code>
          </pre>
          <div className="mt-2 flex items-start gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
            <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>Remember to replace <code className="bg-white px-1 py-0.5 rounded font-mono">CLIENT_ID</code> before using this code.</span>
          </div>
        </div>

        {/* Script Embed */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-agentic-text-primary">Popup Widget</h4>
            <div className='flex items-center'>
              <button
                onClick={() =>
                  openModal("Popup Widget Script", scriptCode)
                }
                className="
    px-3 py-1
    text-[12px] font-medium
    text-black
    bg-white
    rounded-m
    border
    border-black-500
    rounded-md
  "
                title="Select client"
              >
                Select Client
              </button>

            </div>
          </div>
          <p className="text-sm text-agentic-text-secondary mb-3">
            Add as a floating widget. Replace <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">CLIENT_ID</code> with your actual Client ID.
          </p>
          <pre className="p-3 bg-gray-50 border rounded text-xs overflow-auto max-h-48 relative">
            <code>{scriptCode}</code>
          </pre>
          <div className="mt-2 flex items-start gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
            <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>Remember to replace <code className="bg-white px-1 py-0.5 rounded font-mono">CLIENT_ID</code> before using this code.</span>
          </div>
        </div>
      </div>

      {/* Preview Note */}
      <div className="border rounded-lg p-4 bg-blue-50">
        <h4 className="font-medium text-agentic-text-primary mb-2">Preview Note</h4>
        <p className="text-sm text-agentic-text-secondary">
          To test the widget, replace <code className="bg-white px-2 py-0.5 rounded font-mono text-xs">CLIENT_ID</code> in the URL/code with your actual Client ID and open it in a browser or add it to a test page.
        </p>
      </div>
      <ClientSelectModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
        baseValue={modalValue}
        clients={allClients}
      />
    </div>
  );
};

export default ConnectTab;
